import { useMemo } from 'react'
import {
  coalescePitStints,
  driverStints,
  isCaptureLive,
  resolveDriver,
  findCar,
  isInPits,
  lastCrossingOf,
  pitVisitOf,
  stintVendorDriver,
  vendorDriverName,
  type DriverStint,
  type PitStintDriver,
} from '@racedash/shared'
import type { DashboardData } from './useSessionData'
import { rosterDriverFor, type ScreenContext } from './screenContext'

/**
 * This car's driver stints, as the regulations measure them, and the instant
 * to measure the open one against.
 *
 * Shared by every widget that cares about who is in the car and for how long,
 * so they cannot disagree — a stint the budget counts and one the "stop by"
 * tile measures against have to be the same stint.
 *
 * `at` is the session's own clock rather than the viewer's. It stops at the
 * flag and under a red flag, and every duration derived from these stints has
 * to be measured against it: measured against the browser, a stint carries on
 * growing for days after the race, which is how a tile came to say a driver
 * had been in the car for fifty-four hours.
 *
 * While the car is in the pits the open stint is held at its last line
 * crossing. Whether that visit is a change, a penalty or the end of its race is
 * not known until it comes out, and a car that never does should not keep a
 * driver's clock running to the flag.
 */
export function useDriverStints(
  car: string | null,
  data: DashboardData,
  context: ScreenContext,
  now: Date,
): { stints: DriverStint[]; at: Date } {
  const regime = context.plan?.regime
  const at = data.asAt(now)
  const inPits = isInPits(findCar(data.standings, car))

  const stints = useMemo(() => {
    if (!car) return []

    const start =
      data.session?.clockStartAt?.toDate() ??
      data.session?.firstSeenAt?.toDate() ??
      context.sessionStart ??
      null
    if (!start) return []

    const visits = data.pitStops
      .filter((s) => s.car === car)
      .map(pitVisitOf)
      .filter((v): v is NonNullable<typeof v> => v !== null)

    const everyStop = regime?.stops?.driverChangeRequired === 'everyStop'
    const inPitsSince = inPits ? lastCrossingOf(data.laps, car) : null

    /*
     * The stint chart's pit stints, each with the driver the chart shows for
     * it, joined where the driver does not change. The chart is the
     * reference; see coalescePitStints.
     */
    const pitStints = (data.stints.find((d) => d.car === car)?.stints ?? []).filter((p) => p.startedAt)
    if (pitStints.length > 0) {
      const pits: PitStintDriver[] = pitStints.map((p) => {
        const from = p.startedAt!.toDate()
        const to = p.endedAt?.toDate() ?? null
        const vendor = vendorDriverName(stintVendorDriver(p).name)
        const resolved = resolveDriver(context.declarations, car, from, vendor ?? undefined)
        const base = { from, to, lastLap: p.lastLap, laps: p.laps }
        if (resolved.source === 'team') {
          return { ...base, key: resolved.driverId, driverId: resolved.driverId, name: null, declared: true }
        }
        if (resolved.source === 'vendor') {
          const id = rosterDriverFor(context, car, resolved.name)
          return { ...base, key: id ?? `vendor:${resolved.name}`, driverId: id, name: resolved.name, declared: false }
        }
        return { ...base, key: null, driverId: null, name: null, declared: false }
      })
      // A session no longer being captured has no stint in progress: its last
      // one ends where its last lap did, as on the stint chart. Against the
      // dashboard's own clock, which during a replay is the replayed instant.
      const live = !data.session?.finishedAt && isCaptureLive(data.session?.lastSeenAt?.toDate(), now)
      return coalescePitStints(pits, visits, { everyStopChangesDriver: everyStop, inPitsSince, closeLast: !live })
    }

    // Before the interpreter has written any pit stints: from the stops alone.
    return driverStints(car, visits, context.declarations, start, at, {
      everyStopChangesDriver: everyStop,
      inPitsSince,
    })
  }, [car, data.pitStops, data.laps, data.stints, data.session, context, regime, at, inPits, now])

  return { stints, at }
}
